/**
 * Мелкие общие компоненты.
 *
 * Держатся в одном файле сознательно: их пять, каждый в десяток строк, и
 * раскладывать такое по отдельным модулям — плодить импорты на ровном месте.
 */

import { useEffect, useRef, useState } from 'react'
import type { Flag } from '../api/types'
import { IconMinus, IconPlus } from './icons'

export function Loader({ label = 'Загружаю…' }: { label?: string }) {
  return (
    <div className="col" style={{ padding: '48px 0', alignItems: 'center' }}>
      <div className="spinner" />
      <span className="faint">{label}</span>
    </div>
  )
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Что-то пошло не так'
  return (
    <div className="card">
      <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{message}</div>
      {onRetry && (
        <button className="btn wide" onClick={onRetry}>
          Повторить
        </button>
      )}
    </div>
  )
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="card empty">
      <div className="empty-title">{title}</div>
      {hint && <div className="faint">{hint}</div>}
    </div>
  )
}

/**
 * Флаги движка. Порядок задаёт сервер (критичные первыми) — здесь он не
 * пересортировывается: движок лучше знает, что важнее.
 */
export function Flags({ flags, limit }: { flags: Flag[]; limit?: number }) {
  if (!flags.length) return null
  const shown = limit ? flags.slice(0, limit) : flags
  return (
    <div>
      {shown.map((f, i) => (
        <div key={`${f.code}-${f.exercise_id ?? f.muscle_group ?? i}`} className={`flag ${f.severity}`}>
          {f.message}
        </div>
      ))}
    </div>
  )
}

interface StepperProps {
  value: number
  onChange: (next: number) => void
  step?: number
  min?: number
  max?: number
  unit?: string
  /**
   * Максимум знаков после запятой. Хвостовые нули срезаются: одно и то же
   * число не должно выглядеть как «60» в строке подхода и «60.0» в её
   * редакторе — человек решит, что это разные значения.
   */
  decimals?: number
  label?: string
  /**
   * Мелкий шаг — вторая пара кнопок рядом с основной.
   *
   * Нужен весу. Факт в зале не кратен одному шагу: блины бывают по 0.25 и
   * 0.5 кг, гантели идут через килограмм, стек тренажёра размечен в фунтах.
   * С одним шагом 2.5 кг до своего веса было не добраться, и человек
   * записывал ближайший — то есть неправду, из которой потом считаются e1RM
   * и прогноз. Один мелкий шаг тоже не выход: от 70 до 80 кг по 0.25 — сорок
   * нажатий. Поэтому шага два: крупным доезжают, мелким попадают.
   */
  fineStep?: number
}

/**
 * Ввод числа кнопками.
 *
 * Клавиатура в вебвью Telegram на iOS прыгает и перекрывает пол-экрана
 * (раздел 13), а между подходами нужно уложиться в два касания. Поэтому
 * повторы и RPE меняются шагом, а не набором с клавиатуры.
 */
export function Stepper({
  value,
  onChange,
  step = 1,
  min = 0,
  max = 999,
  unit,
  decimals = 0,
  label,
  fineStep,
}: StepperProps) {
  const clamp = (n: number) => Math.min(max, Math.max(min, Number(n.toFixed(decimals + 2))))

  if (fineStep !== undefined) {
    // Кнопки подписаны шагом, а не «−» и «+»: их четыре, и без подписи не
    // понять, какая на сколько. Порядок зеркальный — крупный шаг снаружи.
    const keys = [-step, -fineStep, fineStep, step]
    return (
      <div>
        {label && <div className="stepper-label">{label}</div>}
        <div className="stepper-value">
          <span className="value">{trimZeros(value, decimals)}</span>
          {unit && <span className="unit">{unit}</span>}
        </div>
        <div className="stepper-keys">
          {keys.map((delta) => (
            <button
              key={delta}
              type="button"
              onClick={() => onChange(clamp(value + delta))}
              aria-label={`${delta < 0 ? 'Меньше' : 'Больше'} на ${Math.abs(delta)}${unit ? ` ${unit}` : ''}`}
            >
              {delta < 0 ? '−' : '+'}
              {Math.abs(delta)}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      {label && <div className="stepper-label">{label}</div>}
      <div className="stepper">
        <button type="button" onClick={() => onChange(clamp(value - step))} aria-label="Меньше">
          <IconMinus size={24} />
        </button>
        <div>
          <div className="value">{trimZeros(value, decimals)}</div>
          {unit && <div className="unit">{unit}</div>}
        </div>
        <button type="button" onClick={() => onChange(clamp(value + step))} aria-label="Больше">
          <IconPlus size={24} />
        </button>
      </div>
    </div>
  )
}

/**
 * Кнопка, которая требует второго нажатия.
 *
 * Для необратимого — удалить подход, снять жалобу. Модальное окно в зале
 * между подходами это лишний экран; второе нажатие дешевле и понятнее.
 */
export function ConfirmButton({
  children,
  confirmLabel = 'Точно?',
  onConfirm,
  className = 'btn',
}: {
  children: React.ReactNode
  confirmLabel?: string
  onConfirm: () => void
  className?: string
}) {
  const [armed, setArmed] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        if (armed) {
          window.clearTimeout(timer.current)
          setArmed(false)
          onConfirm()
          return
        }
        setArmed(true)
        timer.current = window.setTimeout(() => setArmed(false), 3000)
      }}
    >
      {armed ? confirmLabel : children}
    </button>
  )
}

/** Число с не более чем `decimals` знаками и без хвостовых нулей. */
function trimZeros(value: number, decimals: number): string {
  if (decimals <= 0) return String(Math.round(value))
  return value
    .toFixed(decimals)
    .replace(/\.?0+$/, '')
}

/** Дата в человеческом виде: «8 сентября», «сегодня», «вчера». */
export function formatDate(iso: string, today = new Date()): string {
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  const days = Math.round((startOfDay(today).getTime() - startOfDay(d).getTime()) / 86_400_000)
  if (days === 0) return 'сегодня'
  if (days === 1) return 'вчера'
  if (days === -1) return 'завтра'
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/**
 * Вес без лишних нулей: 60, 62.5, 71.25.
 *
 * До сотых, а не до десятых: вес вводится с шагом 0.25 кг, и «71.25»,
 * показанное как «71.3», — это уже другой вес на экране, чем в базе.
 */
export function formatWeight(kg: number | null | undefined): string {
  if (kg === null || kg === undefined) return '—'
  return String(Number(kg.toFixed(2)))
}
